import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProductDetailCard from "@/components/ProductDetailCard/ProductDetailCard";
import Nav from "@/components/Header/Nav/Nav";
import Footer from "@/components/Footer/Footer";
import styles from "./ProductDetailPage.module.scss";
import ProductCard from "@/components/ProductCard/ProductCard";
import { useCart } from "@/hooks/useCart";
import CardSlider from "@/components/Slider/Slider";
import { Helmet } from "react-helmet";
import { formatName } from "@/utils/formatName";
import { getSiteConfig } from "@/config/siteConfigs";

const CRM_API = "https://crm.hixa.eu/external/goodsapi/";
const CRM_TOKEN = "DH345DFGDGb32FDsf03876SDFSdf13";

interface Product {
  goid: string;
  catid: string;
  name: string;
  descr: string;
  pricevat: number;
  priceexchvat: number;
  currency: string;
  table: string;
  files: { image?: { [key: number]: string } };
}

const ProductDetailPage: React.FC = () => {
  const { id, section } = useParams<{ id: string; section: string }>();
  const { t } = useTranslation("pages");
  const siteConfig = getSiteConfig();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [similarProducts, setSimilarProducts] = useState<Product[]>([]);
  const { addToCart } = useCart();
  const formattedName = formatName(product?.name || "");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        setError(t("productDetail.errors.invalidId"));
        setLoading(false);
        return;
      }

      try {
        const url =
          section === "service"
            ? `${CRM_API}?table=service&legid=1&language=${siteConfig.language}&type=good&goid=${id}`
            : `${CRM_API}?type=good&goid=${id}&legid=1&language=${siteConfig.language}`;

        const searchResponse = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${CRM_TOKEN}`,
            Accept: "application/json",
          },
        });

        const searchData = await searchResponse.json();
        if (!searchData || searchData.data.length === 0) {
          setError(t("productDetail.errors.notFound"));
        } else {
          const product = searchData.data[0];
          setProduct(product);
          fetchSimilarProducts(product.goid, product.goid);
        }
      } catch (error) {
        setError(t("productDetail.errors.fetchError"));
      } finally {
        setLoading(false);
      }
    };

    const fetchSimilarProducts = async (
      goodid: string,
      currentGoid: string
    ) => {
      try {
        const requestBody: any = {
          type: "good",
          onlyprice: "1",
          start: "0",
          limit: "10",
          goodid: goodid,
          legid: "1",
          language: siteConfig.language,
        };

        if (section === "service") {
          requestBody.table = "service";
        }

        const response = await fetch(CRM_API, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${CRM_TOKEN}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();
        const filtered = data?.data?.filter(
          (item: Product) => item.goid !== currentGoid
        );

        setSimilarProducts(filtered?.slice(0, 5) || []);
      } catch (err) {
        console.error("Error loading similar products:", err);
      }
    };

    fetchProduct();
  }, [id, section, siteConfig.language, t]);

  if (loading) return <p>{t("productDetail.loading")}</p>;
  if (error) return <p>{error}</p>;
  if (!product) return <p>{t("productDetail.errors.missingData")}</p>;

  const title =
    product.name.length > 70 ? `${product.name.slice(0, 67)}...` : product.name;

  const description =
    product.descr.length > 150
      ? `${product.descr.slice(0, 147)}...`
      : product.descr;

  const imageUrls = product.files?.image
    ? Object.values(product.files.image).slice(0, 4)
    : ["/images/Shop/template.webp"];

  const sliderSettings = {
    slidesPerView: 5,
    spaceBetween: 12,
    loop: true,
    breakpoints: {
      1025: {
        slidesPerView: 5,
      },
      478: {
        slidesPerView: 3,
      },
      0: {
        slidesPerView: 2,
      },
    },
  };

  // Функція для генерації стабільного рейтингу на основі product ID
  const getStableRating = (productId: string) => {
    // Простий хеш з ID для отримання числа
    const hash = productId.split("").reduce((acc, char) => {
      return acc + char.charCodeAt(0);
    }, 0);

    // Конвертуємо в число від 4.7 до 5.0
    const rating = 4.7 + (hash % 31) / 100; // 0-30 / 100 = 0.00-0.30
    return rating.toFixed(1);
  };

  // Функція для генерації стабільної кількості відгуків на основі product ID
  const getStableReviewCount = (productId: string) => {
    const hash = productId.split("").reduce((acc, char) => {
      return acc + char.charCodeAt(0);
    }, 0);

    // Конвертуємо в число від 400 до 800
    return 400 + (hash % 401); // 0-400
  };

  const schemaOrgData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.descr,
    sku: product.goid,
    // Додаємо рейтинг та відгуки (стабільні для кожного товару)
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: getStableRating(product.goid),
      bestRating: "5",
      worstRating: "1",
      ratingCount: getStableReviewCount(product.goid),
    },
    offers: {
      "@type": "Offer",
      url: `https://hixa.eu/product/${product.goid}/${formattedName}`,
      priceCurrency: product.currency,
      price: product.pricevat,
      availability: "https://schema.org/InStock",
      seller: {
        "@type": "Organization",
        name: "Hixa",
      },
    },
    image: product.files?.image
      ? Object.values(product.files.image)
      : ["/images/Shop/template.webp"],
  };

  return (
    <div className="pageSection">
      <Helmet>
        <title>
          {title}
          {t("productDetail.meta.titleSuffix")}
        </title>
        <link rel="icon" href="/images/favicon.ico" type="image/x-icon" />
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgData)}
        </script>
      </Helmet>
      <Nav />
      <ProductDetailCard
        id={product.goid}
        catid={product.catid}
        name={product.name}
        pricevat={product.pricevat}
        priceexchvat={product.priceexchvat}
        descr={product.descr}
        images={imageUrls}
        table={product.table}
        onAddToCart={(goid, quantity, table, typeorder) =>
          addToCart(
            {
              goid,
              typeorder,
              table,
            },
            quantity
          )
        }
      />

      {similarProducts.length > 0 && (
        <div className="container">
          <section className={`${styles.similarSection} sideP64`}>
            <h3 className={`brxeHeading sideP24 ${styles.similarTitle}`}>
              {t("productDetail.similarProducts.title")}
            </h3>
            <CardSlider settings={sliderSettings}>
              {similarProducts.map((product) => {
                const productImage = product.files?.image
                  ? Object.values(product.files.image)[0]
                  : "/images/Shop/template.webp";

                return (
                  <ProductCard
                    key={product.goid}
                    goid={product.goid}
                    name={product.name}
                    price={product.pricevat}
                    table={product.table}
                    imageUrl={productImage}
                  />
                );
              })}
            </CardSlider>
          </section>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
